import React, {ReactElement} from "react";
import {Button, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow} from "../../../shared/StyledComponents";
import {DW_SM, PD_LG, PD_MD, PD_SM} from "../../../shared/dimens";
import {
  getActiveCells,
  GRID_SIZE,
  isGap,
  SquareupDifficultyLevel,
  SquareupGameBoard,
  SquareupGameConfig,
  SquareupGameData,
} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {HelpOutlineOutlined, MoreHorizOutlined} from "@mui/icons-material";
import {GAME_SQUAREUP} from "../../Games";
import {Game} from "../../types";
import {colorGreen, colorYellow, mediumGray} from "../../../shared/colors";
import {GameOverResult, GameOverResultType} from "../types";

export type SquareupGameFragmentProps = GameFragmentProps & {}

enum SquareupGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_WON,
  STATUS_LOST,
}

type SquareupGameFragmentState = GameFragmentState<SquareupGameConfig, SquareupGameData> & {
  currentGrid: string[][],
  selectedCell: { row: number, col: number } | null,
  swapsUsed: number,
  gameStatus: SquareupGameStatus,
  message?: string,
}

function TileCell(props: {
  letter: string,
  color: string,
  selected: boolean,
  onClick: () => void,
}): ReactElement {
  return <div
    onClick={props.onClick}
    style={{
      width: 72,
      height: 72,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: props.color || "#e0e0e0",
      border: props.selected ? "3px solid #333" : "1px solid #ccc",
      borderRadius: 4,
      cursor: "pointer",
      userSelect: "none",
      transition: "background 200ms ease",
    }}>
    <Typography variant="h5" style={{
      textTransform: "uppercase",
      fontWeight: "bold",
      pointerEvents: "none",
    }}>{props.letter}</Typography>
  </div>;
}

function GapCell(): ReactElement {
  return <div style={{width: 72, height: 72}}/>;
}

function getTileColor(
  row: number,
  col: number,
  currentGrid: string[][],
  solutionGrid: string[][],
): string {
  const currentLetter = currentGrid[row][col];
  const solutionLetter = solutionGrid[row][col];

  // Green: correct letter at correct position
  if (currentLetter === solutionLetter) {
    return colorGreen;
  }

  // Check which words this tile belongs to
  const belongsToWords: { letters: string[] }[] = [];

  // Horizontal word (rows 0, 2, 4)
  if (row % 2 === 0) {
    belongsToWords.push({letters: Array.from({length: 5}, (_, c) => solutionGrid[row][c])});
  }

  // Vertical word (cols 0, 2, 4)
  if (col % 2 === 0) {
    belongsToWords.push({letters: Array.from({length: 5}, (_, r) => solutionGrid[r * (r <= 2 ? 1 : 1)][col])});
  }

  // Build the solution letters for all words this tile belongs to
  const wordLetters: string[] = [];
  if (row % 2 === 0) {
    for (let c = 0; c < 5; c++) wordLetters.push(solutionGrid[row][c]);
  }
  if (col % 2 === 0) {
    for (let r = 0; r < 5; r++) {
      if (!isGap(r, col)) wordLetters.push(solutionGrid[r][col]);
    }
  }

  // Yellow: letter exists in one of the words for this row/col but at wrong position
  if (wordLetters.includes(currentLetter)) {
    return colorYellow;
  }

  // Gray: not in any relevant word
  return mediumGray;
}

export class SquareupGameFragment extends GameFragment<SquareupGameFragmentProps, SquareupGameConfig, SquareupGameData, SquareupGameFragmentState> {

  private board: SquareupGameBoard;

  protected getGame(): Game {
    return GAME_SQUAREUP;
  }

  protected onCreateState(): SquareupGameFragmentState {
    return {
      ...super.onCreateState(),
      currentGrid: null,
      selectedCell: null,
      swapsUsed: 0,
      gameStatus: SquareupGameStatus.STATUS_IN_PROGRESS,
    };
  }

  componentDidUpdate(prevProps: Readonly<SquareupGameFragmentProps>, prevState: Readonly<SquareupGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData && this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as SquareupGameBoard;
      this.setState({
        currentGrid: this.board.layout.scrambledGrid.map(row => [...row]),
        swapsUsed: 0,
      });
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      gameData: await SquareupGameData.create(gameConfig),
    });
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(GAME_SQUAREUP, <>
      {SquareupDifficultyLevel.VALUES.map(level =>
        <Button variant="contained"
                onClick={() => this.setState({gameConfig: new SquareupGameConfig(level)})}>{level.text}</Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    if (!this.board) return null;
    const swapsRemaining = this.board.level.maxSwaps - this.state.swapsUsed;
    return <Typography style={{marginLeft: PD_MD}}>
      {this.board.level.text} &middot; Swaps: {swapsRemaining}
    </Typography>;
  }

  protected renderToolbarButtons() {
    return <>
      <Button onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, maxWidth: DW_SM, alignItems: "center", gap: PD_LG}}>
          <Typography variant="h5">How to play SquareUp</Typography>
          <Typography>Swap letters on the grid to form 6 correct words (3 across, 3 down).
            Tap a tile, then tap another to swap them.</Typography>
          <StyledBoxColumn style={{gap: PD_SM, alignItems: "start"}}>
            <StyledBoxRow style={{gap: PD_SM, alignItems: "center"}}>
              <div style={{width: 32, height: 32, background: colorGreen, borderRadius: 4}}/>
              <Typography>Correct letter in correct position</Typography>
            </StyledBoxRow>
            <StyledBoxRow style={{gap: PD_SM, alignItems: "center"}}>
              <div style={{width: 32, height: 32, background: colorYellow, borderRadius: 4}}/>
              <Typography>Letter is in the word but wrong position</Typography>
            </StyledBoxRow>
            <StyledBoxRow style={{gap: PD_SM, alignItems: "center"}}>
              <div style={{width: 32, height: 32, background: mediumGray, borderRadius: 4}}/>
              <Typography>Letter is not in the word</Typography>
            </StyledBoxRow>
          </StyledBoxColumn>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </Button>
      <Button onClick={(event) => BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, [])}>
        <MoreHorizOutlined/>
      </Button>
    </>;
  }

  protected renderGameContent(): ReactElement | null {
    if (!this.board || !this.state.currentGrid) {
      return null;
    }
    const {currentGrid, selectedCell, gameStatus, message} = this.state;
    const solutionGrid = this.board.layout.solutionGrid;

    return <StyledBoxColumn style={{flexGrow: 1, width: "100%", maxWidth: 480, margin: "auto", alignItems: "center"}}>
      {message
        ? <Typography style={{
          padding: PD_SM,
          paddingLeft: PD_LG,
          paddingRight: PD_LG,
          background: "#222",
          color: "white",
          borderRadius: 4,
          marginBottom: PD_SM,
        }}>{message}</Typography>
        : <div style={{height: 36}}/>}
      <StyledBoxColumn style={{gap: 4, alignItems: "center"}}>
        {Array.from({length: GRID_SIZE}, (_, row) =>
          <StyledBoxRow key={row} style={{gap: 4}}>
            {Array.from({length: GRID_SIZE}, (_, col) => {
              if (isGap(row, col)) {
                return <GapCell key={col}/>;
              }
              const letter = currentGrid[row][col];
              const color = getTileColor(row, col, currentGrid, solutionGrid);
              const isSelected = selectedCell?.row === row && selectedCell?.col === col;
              return <TileCell
                key={col}
                letter={letter}
                color={color}
                selected={isSelected}
                onClick={() => this.onTileClick(row, col)}/>;
            })}
          </StyledBoxRow>
        )}
      </StyledBoxColumn>
      {gameStatus !== SquareupGameStatus.STATUS_IN_PROGRESS
        ? <StyledBoxColumn style={{alignItems: "center", gap: PD_SM, marginTop: PD_LG}}>
          <Typography variant="h6">
            {gameStatus === SquareupGameStatus.STATUS_WON ? "Congratulations!" : "Out of swaps!"}
          </Typography>
          <Typography>
            {gameStatus === SquareupGameStatus.STATUS_WON
              ? `Solved in ${this.state.swapsUsed} swaps`
              : "Better luck next time"}
          </Typography>
        </StyledBoxColumn>
        : null}
    </StyledBoxColumn>;
  }

  private onTileClick(row: number, col: number) {
    if (this.state.gameStatus !== SquareupGameStatus.STATUS_IN_PROGRESS) return;

    const {selectedCell, currentGrid, swapsUsed} = this.state;
    const solutionGrid = this.board.layout.solutionGrid;

    // Don't allow selecting green (correct) tiles
    if (currentGrid[row][col] === solutionGrid[row][col]) return;

    if (!selectedCell) {
      this.setState({selectedCell: {row, col}});
    } else if (selectedCell.row === row && selectedCell.col === col) {
      // Deselect
      this.setState({selectedCell: null});
    } else {
      // Perform swap
      const newGrid = currentGrid.map(r => [...r]);
      const temp = newGrid[selectedCell.row][selectedCell.col];
      newGrid[selectedCell.row][selectedCell.col] = newGrid[row][col];
      newGrid[row][col] = temp;

      const newSwapsUsed = swapsUsed + 1;
      this.board.swapsUsed = newSwapsUsed;

      // Check win
      const activeCells = getActiveCells();
      const isWon = activeCells.every(c => newGrid[c.row][c.col] === solutionGrid[c.row][c.col]);

      if (isWon) {
        this.props.gameFragmentListener?.onStatusChanged("Game complete.");
        const duration = this.state.gameData.gamePlay.getDuration();
        this.finishGame(new GameOverResult(
          GAME_SQUAREUP.id,
          GameOverResultType.WON,
          this.board.level.name,
          this.board.getPoints(duration),
          duration,
          `Solved in ${newSwapsUsed} swaps!`,
          false
        ));
        this.setState({
          currentGrid: newGrid,
          selectedCell: null,
          swapsUsed: newSwapsUsed,
          gameStatus: SquareupGameStatus.STATUS_WON,
          message: "Solved!",
        });
      } else if (newSwapsUsed >= this.board.level.maxSwaps) {
        // Out of swaps
        this.props.gameFragmentListener?.onStatusChanged("Game complete.");
        this.finishGame(new GameOverResult(
          GAME_SQUAREUP.id,
          GameOverResultType.LOST,
          this.board.level.name,
          0,
          0,
          "Out of swaps.",
          false
        ));
        this.setState({
          currentGrid: newGrid,
          selectedCell: null,
          swapsUsed: newSwapsUsed,
          gameStatus: SquareupGameStatus.STATUS_LOST,
          message: "Out of swaps!",
        });
      } else {
        this.setState({
          currentGrid: newGrid,
          selectedCell: null,
          swapsUsed: newSwapsUsed,
        });
      }
    }
  }
}
